import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { FormField, Input } from '@/components/ui/FormField';
import { Alert } from '@/components/ui/Alert';
import { SelectField } from './SelectField';
import { useAuthStore } from '@/stores/auth';
import { useRecordEligibilityCheck } from '@/api/eligibility';
import { useActivePayers } from '@/api/referenceData';
import type { ClientRecord } from '@/api/clients';

/*
 * Manual eligibility-check entry. The user calls the payer by phone, then
 * captures the result here. Payer + member ID default to the client's
 * primary insurance but remain editable so a secondary-insurance check
 * can be captured without round-tripping through the edit form.
 *
 * When Office Ally (or another clearinghouse) is wired up, the parent
 * section's "Verify Eligibility" button will branch:
 *   - if OA credentials configured → POST a 270 and auto-record the 271
 *   - otherwise → open this dialog for manual capture.
 * The POST endpoint is the same either way; `checkedBy` shifts from the
 * user's name to "Office Ally" (or similar) for automated entries.
 */

const STATUS_OPTIONS = ['Active', 'Inactive', 'Pending'] as const;

interface EligibilityDialogProps {
    client: ClientRecord;
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

type FormState = {
    payer:      string;
    memberId:   string;
    status:     string;
    deductible: string;
    copay:      string;
    authOnFile: boolean;
};

export function EligibilityDialog({ client, open, onOpenChange }: EligibilityDialogProps) {
    const user = useAuthStore((s) => s.user);
    const recordMut = useRecordEligibilityCheck();
    const payersQuery = useActivePayers();
    const payers = payersQuery.data ?? [];

    const [form, setForm] = useState<FormState>(() => initialForm(client));
    const [fieldError, setFieldError] = useState<string | null>(null);

    // If the client's stored payer string doesn't match any configured
    // active payer, surface it as an "(unlisted)" option so the user
    // can still record a check against it without losing their pre-fill.
    const currentInList = payers.some((p) => p.name === form.payer);
    const showUnlisted = !!form.payer && !currentInList;

    useEffect(() => {
        if (open) {
            setForm(initialForm(client));
            setFieldError(null);
            recordMut.reset();
        }
    }, [open, client]); // eslint-disable-line react-hooks/exhaustive-deps

    function update<K extends keyof FormState>(key: K, value: FormState[K]) {
        setForm((f) => ({ ...f, [key]: value }));
    }

    async function onSubmit(e: React.FormEvent) {
        e.preventDefault();
        if (!form.status) {
            setFieldError('Select a coverage status.');
            return;
        }
        setFieldError(null);

        const checkedBy = user ? `${user.first} ${user.last}`.trim() : '';
        try {
            await recordMut.mutateAsync({
                client:     `${client.first} ${client.last}`.trim(),
                payer:      form.payer.trim(),
                memberId:   form.memberId.trim(),
                status:     form.status,
                deductible: form.deductible.trim(),
                copay:      form.copay.trim(),
                authOnFile: form.authOnFile,
                checkedBy,
            });
            onOpenChange(false);
        } catch {
            /* surfaced below */
        }
    }

    const serverError = (() => {
        const e = recordMut.error;
        if (!e) return null;
        if (e instanceof AxiosError) {
            return e.response?.data?.error ?? 'Failed to record eligibility check';
        }
        return 'Unexpected error';
    })();

    const pending = recordMut.isPending;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle>Record Eligibility Check</DialogTitle>
                    <DialogDescription>
                        Capture the outcome of a payer eligibility check. Pre-filled
                        from this client's primary insurance — edit if you verified a
                        different coverage.
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={onSubmit} noValidate>
                    {(serverError || fieldError) && (
                        <Alert variant="danger" className="mb-4">
                            {fieldError ?? serverError}
                        </Alert>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4">
                        <FormField label="Payer">
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.payer}
                                    onChange={(e) => update('payer', e.target.value)}
                                    disabled={payersQuery.isLoading}
                                >
                                    <option value="">
                                        {payersQuery.isLoading ? 'Loading…' : 'Select a payer…'}
                                    </option>
                                    {showUnlisted && (
                                        <option value={form.payer}>
                                            {form.payer} (unlisted)
                                        </option>
                                    )}
                                    {payers.map((p) => (
                                        <option key={p.id} value={p.name}>{p.name}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                        <FormField label="Member ID">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.memberId}
                                    onChange={(e) => update('memberId', e.target.value)}
                                />
                            )}
                        </FormField>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4">
                        <FormField label="Coverage status" required>
                            {(id) => (
                                <SelectField
                                    id={id}
                                    value={form.status}
                                    onChange={(e) => update('status', e.target.value)}
                                >
                                    <option value="">—</option>
                                    {STATUS_OPTIONS.map((s) => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </SelectField>
                            )}
                        </FormField>
                        <FormField label="Deductible">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.deductible}
                                    onChange={(e) => update('deductible', e.target.value)}
                                    placeholder="$500 / $200 met"
                                />
                            )}
                        </FormField>
                        <FormField label="Copay">
                            {(id) => (
                                <Input
                                    id={id}
                                    value={form.copay}
                                    onChange={(e) => update('copay', e.target.value)}
                                    placeholder="$20"
                                />
                            )}
                        </FormField>
                    </div>

                    <label className="inline-flex items-center gap-2 text-sm text-text select-none mt-1">
                        <input
                            type="checkbox"
                            checked={form.authOnFile}
                            onChange={(e) => update('authOnFile', e.target.checked)}
                            className="h-4 w-4 accent-accent"
                        />
                        ABA authorization on file with this payer
                    </label>

                    <DialogFooter>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => onOpenChange(false)}
                            disabled={pending}
                        >
                            Cancel
                        </Button>
                        <Button type="submit" variant="success" disabled={pending}>
                            {pending ? 'Saving…' : 'Save Check'}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function initialForm(client: ClientRecord): FormState {
    return {
        payer:      client.ins1?.payer ?? '',
        memberId:   client.ins1?.id    ?? '',
        status:     '',
        deductible: '',
        copay:      '',
        authOnFile: false,
    };
}
